import { CommonModule } from "@angular/common";
import { Component, inject, signal } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { DocumentableType } from "@models/enums/documentable-type.enum";
import { SupportStatus } from "@models/support";
import { CommentsService } from "@services/comments.service";
import { DocumentsService } from "@services/documents.service";
import { SnackBarService } from "@services/snack-bar.service";
import { SupportsService } from "@services/supports.service";
import { SkeletonModule } from "primeng/skeleton";
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  finalize,
  forkJoin,
  map,
  switchMap,
  tap,
} from "rxjs";
import { AppLayoutComponent } from "../../../shared/components/app-layout/app-layout.component";
import { PageHeaderComponent } from "../../../shared/components/page-header/page-header.component";
import { TaskCommentsComponent } from "../solicitation-detail-page/task-comments/task-comments.component";
import { SupportInfoCardComponent } from "./support-info-card/support-info-card.component";

const SUPPORT_COMMENTABLE_TYPE = "Support";

@Component({
  selector: "app-support-detail-page",
  imports: [
    CommonModule,
    AppLayoutComponent,
    PageHeaderComponent,
    SkeletonModule,
    SupportInfoCardComponent,
    TaskCommentsComponent,
  ],
  templateUrl: "./support-detail-page.component.html",
  styleUrl: "./support-detail-page.component.scss",
})
export class SupportDetailPageComponent {
  private readonly _route = inject(ActivatedRoute);
  private readonly _supportsService = inject(SupportsService);
  private readonly _commentsService = inject(CommentsService);
  private readonly _documentsService = inject(DocumentsService);
  private readonly _snackbarService = inject(SnackBarService);
  private readonly _router = inject(Router);

  private readonly _refreshComments$ = new BehaviorSubject<void>(undefined);

  readonly isSubmittingComment = signal(false);

  private get _supportId(): number {
    return Number(this._route.snapshot.paramMap.get("id"));
  }

  support$ = this._route.paramMap.pipe(
    switchMap((params) =>
      this._supportsService.getById(Number(params.get("id"))),
    ),
  );

  isClosed$ = this.support$.pipe(
    map(
      (support) =>
        support.status === SupportStatus.FINISHED ||
        support.status === SupportStatus.CANCELED,
    ),
  );

  comments$ = this._refreshComments$.pipe(
    switchMap(() =>
      this._commentsService.getAll(SUPPORT_COMMENTABLE_TYPE, this._supportId),
    ),
  );

  goBack() {
    this._router.navigate(["/support"]);
  }

  handleCommentSubmit(event: { message: string; files: File[] }) {
    const comment$ = this._commentsService.create({
      commenter: 0,
      body: event.message,
      commentable_id: this._supportId,
      commentable_type: SUPPORT_COMMENTABLE_TYPE,
    });

    this.isSubmittingComment.set(true);

    comment$
      .pipe(
        switchMap((comment) => {
          const uploads$ = event.files.map((file) =>
            this._documentsService.upload(
              DocumentableType.COMMENT,
              comment.id,
              file,
            ),
          );
          return uploads$.length ? forkJoin(uploads$) : EMPTY;
        }),
        tap({ complete: () => this._refreshComments$.next() }),
        catchError(() => {
          this._snackbarService.openSnackBar(
            "Não foi possível enviar o comentário.",
            "Fechar",
            "error",
          );
          return EMPTY;
        }),
        finalize(() => this.isSubmittingComment.set(false)),
      )
      .subscribe();
  }
}
